import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CustomColumnsDataUpdate,
  CustomColumnsUpdate,
  FilterBox,
  getAllSettingsOk,
  TableSaveAsSettingsDTO,
  TableDeleteSettingsDTO,
  TableFilterBoxChangeDTO,
  TablePatchSettingsDTO,
  TableSettingsOkDTO,
  TableState,
  TableSwitchFilterDTO,
  TableUpdateSelectionDTO,
  TableSaveSettingsDTO,
  NotifiablesUpdateDTO,
  Notifiable,
  NotifyDTO,
  CustomColumnsDataDelete,
  CustomColumnsDataGet,
  LoadSettingsDTO,
  CustomColumn,
  FilteredIdsUpdateDTO,
} from './types';
import { TableSettings } from '../../components/MyGridRedux/types';
import { State } from '@progress/kendo-data-query';
import { actionOption } from '../../components/types';
import { FormType } from '../form/types';
import { TableFriendlyName } from '../info/types';
import { unserializeSettings } from './helpers';

export const initialState: TableState = {
  tablesSettings: {},
  otherTablesSettings: {},
  loading: false,
  selectedRowIds: {
    submissions: [],
    papers_to_reviews: [],
    reviews_to_papers: [],
    reviewers_bids: [],
    submissions_bids: [],
    submissions_to_review: [],
    invitations: [],
    users: [],
    reviewer_bids: [],
    submission_bids: [],
    reviewer_assignments: [],
    submission_assignments: [],
    reviewers_to_reviewers: [],
    reviewer_assignments_of_reviewers: [],
    people_to_review: [],
  },
  action: '',
  customColumnsData: {},
  customColumnsById: {},
  notifiableIds: {},
  gridForms: {},
  filteredIds: {
    'App\\Models\\Submission': undefined,
    'App\\Models\\Person': undefined,
    'App\\Models\\Invitation': undefined,
    reviewers_to_reviewers: undefined,
  },
};

// @ts-ignore
export const getInitialDataState = (): State => ({ skip: 0, take: 20, filter: null });
export const getInitialFilterBox = (): FilterBox => ({ query: '', expressions: [] });

const tableSlice = createSlice({
  name: 'TABLE',
  initialState,
  reducers: {
    reset: () => initialState,
    ['ALL_SETTINGS:GET']: (state, action: PayloadAction<TableFriendlyName | undefined>) => ({
      ...state,
      loading: true,
    }),
    ['ALL_SETTINGS:GET:OK']: (state, action: PayloadAction<getAllSettingsOk>) => {
      const ts: { [key: string]: TableSettings } = {};
      const ots: { [key: string]: TableSettings[] } = {};

      // Distribute current and others settings to their corresponding keys
      action.payload.tablesSettings.forEach((tablesettings) => {
        if (tablesettings.is_current) {
          let dataStateToKeep;
          if (state.tablesSettings[tablesettings.friendlyName]) {
            const { skip } = state.tablesSettings[tablesettings.friendlyName].gridSettings.dataState;
            dataStateToKeep = { skip }; // Keep "skip" field of the current dataState value
          } else {
            dataStateToKeep = {};
          }

          // Fresh creation
          ts[tablesettings.friendlyName] = {
            ...unserializeSettings(tablesettings, dataStateToKeep),
            filterBox: getInitialFilterBox(),
            externalChangeCount: 0,
          };
        } else {
          if (!(tablesettings.friendlyName in ots)) {
            ots[tablesettings.friendlyName] = [];
          }
          ots[tablesettings.friendlyName].push({
            ...tablesettings,
          });
        }
      });

      const tablesSettings = { ...state.tablesSettings };
      Object.keys(ts).forEach((friendlyName) => {
        tablesSettings[friendlyName] = ts[friendlyName];
      });

      const otherTablesSettings = { ...state.otherTablesSettings };
      Object.keys(ots).forEach((friendlyName) => {
        otherTablesSettings[friendlyName] = ots[friendlyName];
      });

      /* Update only those custom columns belonging to any of the tables in the action */
      // Remove custom columns affected
      const touched = new Set(action.payload.tablesSettings.map((tablesettings) => tablesettings.friendlyName));
      let customColumnsById: { [key: string]: CustomColumn } = {};
      Object.values(state.customColumnsById)
        .filter((customColumn) => !touched.has(customColumn.tableFriendlyName))
        .forEach((customColumn) => {
          customColumnsById[customColumn.id] = customColumn;
        });
      //Now merge current state of custom columns with the information from the api response */
      customColumnsById = { ...customColumnsById, ...action.payload.customColumnsById };

      return {
        ...state,
        tablesSettings,
        otherTablesSettings,
        customColumnsById,
        loading: false,
      };
    },
    ['SETTINGS:PATCH']: (state, _action: PayloadAction<TablePatchSettingsDTO>) => ({
      ...state,
    }),
    ['SETTINGS:PATCH:OK']: (state, action: PayloadAction<TableSettingsOkDTO>) => {
      const { tableSettings } = action.payload;
      return {
        ...state,
        loading: false,
        tablesSettings: {
          ...state.tablesSettings,
          [tableSettings.friendlyName]: {
            ...tableSettings,
          },
        },
      };
    },
    ['SETTINGS:SAVE']: (state, _action: PayloadAction<TableSaveSettingsDTO>) => ({
      ...state,
    }),
    ['SETTINGS:SAVE_AS']: (state, _action: PayloadAction<TableSaveAsSettingsDTO>) => ({
      ...state,
    }),
    ['SETTINGS:LOAD']: (state, _action: PayloadAction<LoadSettingsDTO>) => ({
      ...state,
      loading: true,
    }),
    ['SETTINGS:LOAD:OK']: (state, action: PayloadAction<TableSettingsOkDTO>) => {
      const { tableSettings } = action.payload;
      return {
        ...state,
        loading: false,
        tablesSettings: {
          ...state.tablesSettings,
          [tableSettings.friendlyName]: tableSettings,
        },
      };
    },
    ['SETTINGS:DELETE']: (state, _action: PayloadAction<TableDeleteSettingsDTO>) => ({
      ...state,
    }),
    ['FILTERBOX:CHANGE']: (state, action: PayloadAction<TableFilterBoxChangeDTO>) => ({
      ...state,
      tablesSettings: {
        ...state.tablesSettings,
        [action.payload.friendlyName]: {
          ...state.tablesSettings[action.payload.friendlyName],
          filterBox: action.payload.filterBox,
        },
      },
    }),
    ['SWITCH_FILTER']: (state, action: PayloadAction<TableSwitchFilterDTO>) => ({
      ...state,
    }),
    ['SELECTION:UPDATE']: (state, action: PayloadAction<TableUpdateSelectionDTO>) => ({
      ...state,
      selectedRowIds: { ...state.selectedRowIds, [action.payload.tableFriendlyName]: action.payload.rowIds },
    }),
    ['SELECTION:CLEAR']: (state) => ({
      ...state,
      selectedRowIds: initialState.selectedRowIds,
    }),
    ['ACTION:SET']: (state, action: PayloadAction<actionOption>) => ({
      ...state,
      action: action.payload,
    }),
    ['CUSTOMCOLUMNS:DATA:GET']: (state, action: PayloadAction<CustomColumnsDataGet>) => ({
      ...state,
      loading: true,
    }),
    ['CUSTOMCOLUMNS:DATA:GET:OK']: (state, action: PayloadAction<CustomColumnsDataUpdate>) => ({
      ...state,
      loading: false,
      customColumnsData: {
        ...state.customColumnsData,
        [action.payload.friendlyName]: action.payload.byId,
      },
    }),
    ['CUSTOMCOLUMNS:DATA:DELETE']: (state, action: PayloadAction<CustomColumnsDataDelete>) => {
      const { [action.payload.friendlyName]: _, ...rest } = state.customColumnsData;
      return {
        ...state,
        customColumnsData: rest,
      };
    },
    ['CUSTOMCOLUMNS:UPDATE']: (state, action: PayloadAction<CustomColumnsUpdate>) => ({
      ...state,
    }),
    ['NOTIFIABLES:UPDATE']: (state, action: PayloadAction<NotifiablesUpdateDTO>) => {
      const { assignment_id, assigned } = action.payload;
      let newNotifiableIds: {
        [id: number]: Notifiable;
      };
      if (!assigned) {
        // Clear notification
        const { [assignment_id]: value, ...rest } = state.notifiableIds;
        newNotifiableIds = rest;
      } else if (!(assignment_id in state.notifiableIds)) {
        // Create a notification
        newNotifiableIds = {
          ...state.notifiableIds,
          [assignment_id]: { isDone: false },
        };
      } else if (!state.notifiableIds[assignment_id].isDone) {
        // Set isDone to true
        newNotifiableIds = {
          ...state.notifiableIds,
          [assignment_id]: { ...state.notifiableIds[assignment_id], isDone: true },
        };
      } else {
        newNotifiableIds = state.notifiableIds;
      }
      return { ...state, notifiableIds: newNotifiableIds };
    },
    ['NOTIFIABLES:CLEAR']: (state) => {
      return { ...state, notifiableIds: initialState.notifiableIds };
    },
    ['NOTIFIABLES:NOTIFY']: (state, action: PayloadAction<NotifyDTO>) => {
      return {
        ...state,
      };
    },
    ['GRIDFORMS:GET:OK']: (state, action: PayloadAction<{ [key: string]: { [key: string]: FormType[] } }>) => {
      return {
        ...state,
        gridForms: action.payload,
      };
    },
    ['FILTERED_IDS:UPDATE']: (state, action: PayloadAction<FilteredIdsUpdateDTO>) => {
      const { ids, key } = action.payload;
      return {
        ...state,
        filteredIds: {
          ...state.filteredIds,
          [key]: ids,
        },
      };
    },
    ['EXTERNAL_CHANGE_COUNT:RESET']: (state, action: PayloadAction<TableFriendlyName>) => {
      return {
        ...state,
        tablesSettings: {
          ...state.tablesSettings,
          [action.payload]: { ...state.tablesSettings[action.payload], externalChangeCount: 0 },
        },
      };
    },
    ['EXTERNAL_CHANGE_COUNT:INCREMENT']: (state, action: PayloadAction<TableFriendlyName>) => {
      return {
        ...state,
        tablesSettings: {
          ...state.tablesSettings,
          [action.payload]: {
            ...state.tablesSettings[action.payload],
            externalChangeCount: state.tablesSettings[action.payload].externalChangeCount + 1,
          },
        },
      };
    },
  },
});

export default tableSlice;
